import { ModernNav } from '@/components/ModernNav';
import { HeroSection } from '@/components/HeroSection';
import { StatsSection } from '@/components/StatsSection';
import { BenefitsSection } from '@/components/BenefitsSection';
import { HowItWorksSection } from '@/components/HowItWorksSection';
import { ServicesSection } from '@/components/ServicesSection';
import { ReviewsSection } from '@/components/ReviewsSection';
import { LogoMarquee } from '@/components/LogoMarquee';
import { BlogSection } from '@/components/BlogSection';
import { SEOHead } from '@/components/SEOHead';
import { organizationSchema, localBusinessSchema } from '@/utils/structuredData';
import { Link } from 'react-router-dom';
const Index = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [organizationSchema, localBusinessSchema]
  };

  return <>
    <SEOHead 
      title="Gold Coast Mobile Mechanic | Professional Car Service"
      description="Gold Coast's #1 mobile mechanic. Professional car servicing, diagnostics & repairs at your location. Book online or call +61 440 134 765. Same day service available."
      keywords="mobile mechanic Gold Coast, car service, automotive repair, pre-purchase inspection, diagnostics, brake repair, battery replacement, mobile auto service"
      canonical="https://www.carguroo.com.au/"
      structuredData={structuredData}
    />
    <div className="min-h-screen">
      <ModernNav />
      
      <main className="relative">
        {/* 1. Hero Section - Clear value proposition */}
        <HeroSection />
        
        {/* 2. Benefits Section - What's in it for them */}
        <BenefitsSection />
        
        {/* 3. How It Works - Process explanation */}
        <HowItWorksSection />
        
        {/* 4. Stats Section - Social proof & credibility */}
        <StatsSection />
        
        {/* 5. Services Section - The offer/products */}
        <ServicesSection />
        
        {/* 6. Logo Marquee - Trust & credibility through partners */}
        <LogoMarquee />
        
        {/* 7. Reviews Section - More social proof after offer */}
        <ReviewsSection />
        
        {/* 8. Blog Section - Additional value/SEO */}
        <BlogSection />
        
      </main>
      
      {/* SEO-optimized Footer with semantic HTML */}
      <footer className="bg-card/90 text-foreground relative overflow-hidden hero-pattern" role="contentinfo">
        
        <section className="py-8 lg:py-16 mobile-section-spacing relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Services Navigation */}
              <nav aria-label="Footer Services">
                <h3 className="text-lg font-bold mb-6 text-primary">Our Services</h3>
                <ul className="space-y-3">
                  <li><Link to="/services/mobile-repairs" className="text-muted-foreground hover:text-primary transition-colors">Mobile Auto Repairs</Link></li>
                  <li><Link to="/services/car-servicing" className="text-muted-foreground hover:text-primary transition-colors">Mobile Car Servicing</Link></li>
                  <li><Link to="/services/diagnostics" className="text-muted-foreground hover:text-primary transition-colors">Vehicle Diagnostics</Link></li>
                  <li><Link to="/services/pre-purchase-inspection" className="text-muted-foreground hover:text-primary transition-colors">Pre-Purchase Inspections</Link></li>
                  <li><Link to="/services/mobile-repairs" className="text-muted-foreground hover:text-primary transition-colors">Brake Services</Link></li>
                  <li><Link to="/services/mobile-repairs" className="text-muted-foreground hover:text-primary transition-colors">Battery Replacement</Link></li>
                </ul>
              </nav>

              {/* Contact Information */}
              <address className="not-italic">
                <h3 className="text-lg font-bold mb-6 text-primary">Contact Info</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-foreground font-semibold">
                      <a href="tel:+61440134765" className="hover:text-primary transition-colors">+61 440 134 765</a>
                    </p>
                    <p className="text-muted-foreground text-sm">Mon-Fri 7:30am-4pm</p>
                  </div>
                  
                  <div>
                    <p className="text-foreground font-semibold">
                      <a href="mailto:info@carguroo.com.au" className="hover:text-primary transition-colors">info@carguroo.com.au</a>
                    </p>
                    <p className="text-muted-foreground text-sm">Quick response guaranteed</p>
                  </div>
                  
                  <div>
                    <p className="text-foreground font-semibold">Gold Coast</p>
                    <p className="text-muted-foreground text-sm">& surrounding areas</p>
                  </div>
                </div>
              </address>

              {/* Business Hours */}
              <div>
                <h3 className="text-lg font-bold mb-6 text-primary">Business Hours</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monday - Friday</span>
                    <span className="text-foreground font-semibold">7:30am - 4:00pm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Saturday</span>
                    <span className="text-foreground font-semibold">By appointment</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sunday</span>
                    <span className="text-muted-foreground">Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom Bar */}
        <div className="border-t border-border py-4 lg:py-8 mobile-touch-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-center md:text-left">
                <p className="text-muted-foreground">
                  © 2024 Carguroo Mobile Mechanic Services. All rights reserved.
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  Licensed, Insured & Professional Automotive Services
                </p>
              </div>
              
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-primary font-bold text-lg">1000+</div>
                  <div className="text-muted-foreground text-xs">Happy Customers</div>
                </div>
                
                <div className="text-center">
                  <div className="text-primary font-bold text-lg">4.9★</div>
                  <div className="text-muted-foreground text-xs">Customer Rating</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </>;
};
export default Index;